import { QuestionCircleOutlined } from "@ant-design/icons";
import {
	Button,
	Form,
	Input,
	Modal,
	message,
	Radio,
	Select,
	Tooltip,
} from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import { useCreateManualReservation } from "../hooks";
import type { EventOccurrence } from "../types";
import { toJSTDayjs } from "../utils/timezoneUtils";

const { TextArea } = Input;
const { Option } = Select;

interface ManualReservationModalProps {
	open: boolean;
	onClose: () => void;
	occurrences: EventOccurrence[];
	eventTitle?: string;
	primaryColor?: string;
	primaryLightColor?: string;
}

interface GroupedOccurrence {
	date: string;
	occurrences: EventOccurrence[];
}

const formatDate = (dateStr: string): string => {
	return toJSTDayjs(dateStr).format("YYYY-MM-DD");
};

const formatTime = (dateStr: string): string => {
	return toJSTDayjs(dateStr).format("HH:mm");
};

const getLeftSlots = (max: number, expected: number): number => {
	return Math.max(0, max - expected);
};

export const ManualReservationModal: React.FC<ManualReservationModalProps> = ({
	open,
	onClose,
	occurrences,
	eventTitle: _eventTitle = "",
	primaryColor = "#99CA29",
	primaryLightColor = "#fdfff0",
}) => {
	const [form] = Form.useForm();
	const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<
		number | undefined
	>();
	const [groupedOccurrences, setGroupedOccurrences] = useState<
		GroupedOccurrence[]
	>([]);
	const [availableDates, setAvailableDates] = useState<string[]>([]);

	const createReservationMutation = useCreateManualReservation();

	useEffect(() => {
		if (!open) {
			form.resetFields();
			setSelectedOccurrenceId(undefined);
			setGroupedOccurrences([]);
			setAvailableDates([]);
		}
	}, [open, form]);

	useEffect(() => {
		if (occurrences && occurrences.length > 0) {
			const now = new Date();
			const groups: Record<string, EventOccurrence[]> = {};
			occurrences
				.filter((occ) => new Date(occ.startAt) > now)
				.forEach((occ) => {
					const date = formatDate(occ.startAt);
					if (!groups[date]) {
						groups[date] = [];
					}
					groups[date].push(occ);
				});

			const groupedArray = Object.keys(groups)
				.sort()
				.map((date) => ({
					date,
					occurrences: groups[date].sort(
						(a, b) =>
							new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
					),
				}));

			setGroupedOccurrences(groupedArray);
			setAvailableDates(groupedArray.map((g) => g.date));
		}
	}, [occurrences, open]);

	const handleDateChange = (_date: string) => {
		form.setFieldValue("occurrenceId", undefined);
		setSelectedOccurrenceId(undefined);
	};

	const handleSubmit = async (values: Record<string, unknown>) => {
		if (!selectedOccurrenceId) {
			message.error("予約時間を選択してください");
			return;
		}

		try {
			await createReservationMutation.mutateAsync({
				occurrenceId: selectedOccurrenceId,
				lastName: values.lastName as string,
				firstName: values.firstName as string,
				lastNameKana: values.lastNameKana as string,
				firstNameKana: values.firstNameKana as string,
				telephone: values.telephone as string,
				email: values.email as string | undefined,
				postalCode: values.postalCode as string | undefined,
				address: values.address as string | undefined,
				building: values.building as string | undefined,
				message: values.memo as string | undefined,
			});

			message.success("予約を登録しました");
			onClose();
		} catch (error) {
			if (error instanceof Error) {
				message.error(error.message);
			} else {
				message.error("予約の登録に失敗しました");
			}
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title="手作業での予約"
			footer={null}
			width={720}
			styles={{
				body: {
					maxHeight: "90vh",
					overflowY: "auto",
					overflowX: "hidden",
				},
			}}
			maskClosable={false}
			destroyOnClose
			centered
		>
			<Form
				form={form}
				layout="vertical"
				size="large"
				requiredMark={false}
				onFinish={handleSubmit}
			>
				<div
					style={{
						padding: "8px 16px",
						textAlign: "center",
						fontSize: "18px",
						fontWeight: "bold",
						marginBottom: 16,
						borderRadius: 4,
						backgroundColor: primaryLightColor,
						color: primaryColor,
					}}
				>
					予約日
				</div>

				<Form.Item
					name="date"
					rules={[{ required: true, message: "予約日を選択してください" }]}
				>
					<Select
						placeholder="予約日を選択してください"
						onChange={handleDateChange}
						notFoundContent="予約可能な日程がありません"
					>
						{availableDates.map((date) => (
							<Option key={date} value={date} style={{ textAlign: "center" }}>
								{date}
							</Option>
						))}
					</Select>
				</Form.Item>

				<div
					style={{
						padding: "8px 16px",
						textAlign: "center",
						fontSize: "18px",
						fontWeight: "bold",
						marginBottom: 16,
						borderRadius: 4,
						backgroundColor: primaryLightColor,
						color: primaryColor,
					}}
				>
					予約時間
				</div>

				<Form.Item
					noStyle
					shouldUpdate={(prevValues, currentValues) =>
						prevValues.date !== currentValues.date
					}
				>
					{({ getFieldValue }) =>
						getFieldValue("date") ? (
							<Form.Item
								name="occurrenceId"
								rules={[{ required: true, message: "時間を選択してください" }]}
							>
								<Radio.Group
									style={{ width: "100%" }}
									onChange={(e) => setSelectedOccurrenceId(e.target.value)}
								>
									<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
										{groupedOccurrences
											.find((g) => g.date === getFieldValue("date"))
											?.occurrences.map((occ) => {
												const leftSlots = getLeftSlots(
													occ.maxAttendees,
													occ.sumExpected,
												);
												const isFull = leftSlots === 0;
												const isAlmostFull = leftSlots <= 3 && !isFull;

												return (
													<div
														key={occ.occurrenceId}
														style={{
															width: "calc(50% - 4px)",
															border:
																selectedOccurrenceId === occ.occurrenceId
																	? `2px solid ${primaryColor}`
																	: "1px solid #d9d9d9",
															borderRadius: 4,
															backgroundColor:
																selectedOccurrenceId === occ.occurrenceId
																	? primaryLightColor
																	: "white",
															padding: 8,
														}}
													>
														<Radio
															value={occ.occurrenceId}
															disabled={isFull}
															style={{ width: "100%" }}
														>
															<div>
																<div style={{ fontWeight: "bold" }}>
																	{formatTime(occ.startAt)} ~{" "}
																	{formatTime(occ.endAt)}
																</div>
																<div
																	style={{
																		fontSize: 12,
																		color: isFull
																			? "#dc2626"
																			: isAlmostFull
																				? "#f59e0b"
																				: "#10b981",
																	}}
																>
																	{isFull
																		? "満席"
																		: isAlmostFull
																			? `残り${leftSlots}席`
																			: `空席あり (${leftSlots}席)`}
																</div>
															</div>
														</Radio>
													</div>
												);
											})}
									</div>
								</Radio.Group>
							</Form.Item>
						) : (
							<p style={{ textAlign: "center", margin: "16px 0" }}>
								予約日を選択してください
							</p>
						)
					}
				</Form.Item>

				<div
					style={{
						padding: "8px 16px",
						textAlign: "center",
						fontSize: "18px",
						fontWeight: "bold",
						marginBottom: 16,
						borderRadius: 4,
						backgroundColor: primaryLightColor,
						color: primaryColor,
					}}
				>
					お客様情報
				</div>

				<Form.Item required label="氏名（漢字）" style={{ marginBottom: 0 }}>
					<div style={{ display: "flex", gap: 8 }}>
						<Form.Item
							name="lastName"
							style={{ width: "calc(50% - 4px)" }}
							rules={[
								{
									required: true,
									whitespace: true,
									message: "姓を入力してください",
								},
								{ max: 50, message: "50文字未満である必要があります" },
							]}
						>
							<Input placeholder="例：山田" />
						</Form.Item>
						<Form.Item
							name="firstName"
							style={{ width: "calc(50% - 4px)" }}
							rules={[
								{
									required: true,
									whitespace: true,
									message: "名を入力してください",
								},
								{ max: 50, message: "50文字未満である必要があります" },
							]}
						>
							<Input placeholder="例：太郎" />
						</Form.Item>
					</div>
				</Form.Item>

				<Form.Item
					required
					label="氏名（フリガナ）"
					style={{ marginBottom: 0 }}
				>
					<div style={{ display: "flex", gap: 8 }}>
						<Form.Item
							name="lastNameKana"
							style={{ width: "calc(50% - 4px)" }}
							rules={[
								{
									required: true,
									whitespace: true,
									message: "姓を入力してください",
								},
								{ max: 50, message: "50文字未満である必要があります" },
								{
									pattern: /^[\u30a0-\u30ff]+$/,
									message: "全角カタカナで入力してください",
								},
							]}
						>
							<Input placeholder="例：ヤマダ" />
						</Form.Item>
						<Form.Item
							name="firstNameKana"
							style={{ width: "calc(50% - 4px)" }}
							rules={[
								{
									required: true,
									whitespace: true,
									message: "名を入力してください",
								},
								{ max: 50, message: "50文字未満である必要があります" },
								{
									pattern: /^[\u30a0-\u30ff]+$/,
									message: "全角カタカナで入力してください",
								},
							]}
						>
							<Input placeholder="例：タロウ" />
						</Form.Item>
					</div>
				</Form.Item>

				<Form.Item
					label="電話番号"
					name="telephone"
					rules={[
						{ max: 15, message: "電話番号は15数字未満である必要があります" },
					]}
				>
					<Input placeholder="例：080-0000-0000" allowClear />
				</Form.Item>

				<Form.Item
					label="メールアドレス"
					name="email"
					rules={[
						{
							type: "email",
							message: "有効なメールアドレスを入力してください",
						},
					]}
				>
					<Input placeholder="例：yamada@pregio.co.jp" allowClear />
				</Form.Item>

				<Form.Item name="postalCode" label="郵便番号">
					<Input placeholder="例：5670000" maxLength={7} allowClear />
				</Form.Item>

				<Form.Item label="住所" style={{ marginBottom: 0 }}>
					<Form.Item name="address">
						<TextArea placeholder="大阪市中央区南船場1-18-11" autoSize />
					</Form.Item>
					<Form.Item name="building">
						<TextArea placeholder="SRビル長堀12階" autoSize />
					</Form.Item>
				</Form.Item>

				<div
					style={{
						padding: "8px 16px",
						textAlign: "center",
						fontSize: "18px",
						fontWeight: "bold",
						marginBottom: 16,
						borderRadius: 4,
						backgroundColor: primaryLightColor,
						color: primaryColor,
					}}
				>
					メッセージ
					<Tooltip title="最大200文字です。">
						<QuestionCircleOutlined
							style={{ color: primaryColor, marginLeft: 8, fontSize: 14 }}
						/>
					</Tooltip>
				</div>

				<Form.Item
					name="memo"
					rules={[
						{ max: 200, message: "メッセージは200文字以内にしてください" },
					]}
				>
					<TextArea
						autoSize
						placeholder="追記事項やご質問がございましたら、ご入力ください。"
					/>
				</Form.Item>

				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 16,
						marginTop: 24,
					}}
				>
					<Button
						type="primary"
						htmlType="submit"
						loading={createReservationMutation.isPending}
						style={{
							width: 320,
							height: 48,
							backgroundColor: primaryColor,
							borderColor: primaryColor,
						}}
					>
						この内容で登録する
					</Button>
					<Button onClick={onClose} style={{ width: 128 }}>
						閉じる
					</Button>
				</div>
			</Form>
		</Modal>
	);
};
